import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { BaixaService } from '../../services/movimento/baixa.service';
import { ContaService } from '../../services/conta/conta.service';
import { BaixaDTO, BaixaResponseDTO } from '../../models/gerenciar-contas/baixa.model';
import { TipoTransacao } from '../../enums/TipoTransacao.enum';
import { TipoDeDespesa, TIPOS_DESPESA_NAO_EXIBIR } from '../../enums/TipoDespesa.enum';
import { TipoDeReceita } from '../../enums/TipoReceita.enum';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../models/configuracoes/empresa.model';
import { forkJoin, Subscription } from 'rxjs';
import { TableColumn } from '../../models/shared/table-column.model';
import { TranslateService } from '@ngx-translate/core';
import { TipoDespesaService } from '../../services/enums/tipo-despesa.service';
import { TipoReceitaService } from '../../services/enums/tipo-receita.service';
import { PeriodoFiltro, TipoPeriodo } from '../../models/dashboard/periodo-filtro.model';
import { calcularPeriodoPredefinido, fmtDateTime } from '../../shared/utils/periodo.utils';
import { EntitySelectorConfig } from '../../shared/components/entity-selector/entity-selector.interfaces';
import { PessoaDTO } from '../../models/clientes/pessoa.model';
import { PermissaoService } from '../../services/permissao/permissao.service';
import { moedasCompativeis } from '../../utils/moeda-pareamento.util';
import { ExtratoDeCaixaPdfParams } from '../../models/movimento/extrato-de-caixa-pdf.model';
import { FileUtils } from '../../shared/utils/file-utils';

@Component({
  selector: 'app-movimentacao',
  templateUrl: './movimentacao.component.html',
  styleUrls: ['./movimentacao.component.scss'],
})
export class MovimentacaoComponent implements OnInit, OnDestroy {
  readonly STORAGE_KEY = 'movimentacao-visible-columns';

  // Sem paginacao: carregamos todos os movimentos do periodo em uma unica chamada.
  // O backend aceita size grande via Pageable; usamos um valor alto para abranger
  // o maior periodo esperado em producao.
  private readonly SIZE_SEM_PAGINACAO = 100000;

  baixas: BaixaDTO[] = [];
  loading = false;

  /** Controla o estado de geracao do PDF do extrato (previne cliques duplicados). */
  exportandoPdf = false;

  // Estado de ordenacao server-side (padrao UsuarioComponent)
  lastfield: string = 'emissao';
  lastSort: string = 'asc';

  contasFull: any[] = [];
  totalGuarani = 0;
  totalDolar = 0;
  totalReal = 0;
  totalPeso = 0;
  loadingTotais = false;
  empresa: EmpresaConfigDTO | null = null;
  searchNome = '';
  loadingContas = false;
  contas: { label: string; value: number; flagUrl: string }[] = [];
  contaSelecionada: number | null = null;

  // Sidebar de filtros avancados
  sidebarFiltrosVisible = false;
  filtroMoedaSelecionada: string | null = null;
  filtroDescricao = '';
  filtroTipoTransacao: string | null = null;
  tipoTransacaoOptions: { label: string; value: string }[] = [];

  // Filtro de pessoa
  filtroPessoaId: number | null = null;
  filtroPessoaSelecionada: PessoaDTO | undefined;
  filtroPessoaSelectorConfig!: EntitySelectorConfig;

  // Filtros de tipo de gasto e tipo de ingresso
  filtroTipoDespesa: string | null = null;
  filtroTipoReceita: string | null = null;
  tiposDespesaFiltro: { label: string; value: string; icone?: string; cor?: string }[] = [];
  tiposReceitaFiltro: { label: string; value: string; icone?: string; cor?: string }[] = [];
  tipoDespesaMap: Map<string, { icone: string; cor: string }> = new Map();
  tipoReceitaMap: Map<string, { icone: string; cor: string }> = new Map();

  filters = {
    buscaGeral: '',
    contaId: null as number | null,
    dataInicio: null as string | null,
    dataFim: null as string | null
  };

  // Periodo filtro (componente compartilhado)
  periodoSelecionado: PeriodoFiltro = {
    tipo: 'today',
    dataInicio: '',
    dataFim: '',
    texto: ''
  };

  // Objetos de traducao
  texts = {
    titulo: '',
    subtitulo: '',
    filtros: '',
    conta: '',
    selecione_periodo: '',
    total_guarani: '',
    total_dolar: '',
    total_real: '',
    total_peso: '',
    saldo_positivo: '',
    saldo_negativo: '',
    movimentacoes: '',
    totais_do_periodo: '',
    nenhuma_movimentacao: '',
    ajuste_filtros: '',
    carregando: '',
    processando: '',
    sem_nome: '',
    nao_identificado: '',
    filtros_avancados: '',
    filtros_avancados_subtitulo: '',
  };

  labels = {
    data_emissao: '',
    codigo: '',
    descricao: '',
    informacao: '',
    pessoa: '',
    tipo_transacao: '',
    entrada: '',
    saida: '',
    filtro_tipo_despesa: '',
    filtro_tipo_receita: '',
    acoes: '',
  };

  placeholders = {
    pesquisar: '',
    selecionar: '',
  };

  tooltips = {
    editar: '',
    ver_fatura: '',
  };

  botoes = {
    buscar: '',
    imprimir_relatorio: '',
    limpar_filtros: '',
    filtrar: '',
  };

  private langChangeSub!: Subscription;
  tiposTransacao = Object.values(TipoTransacao);
  totalEntradasPeriodo = 0;
  totalSaidasPeriodo = 0;

  // OTIMIZAÇÃO 3: Cache de visibilidade de colunas para evitar find() repetido
  private columnVisibilityCache: Map<string, boolean> = new Map();

  /** Configuracao de colunas visiveis da tabela */
  tableColumns: TableColumn[] = [
    { key: 'emissao', labelKey: 'Data_Emissao', visible: true, fixed: true, defaultVisible: true },
    { key: 'codigoBaixa', labelKey: 'Codigo', visible: true, fixed: false, defaultVisible: true },
    { key: 'descricao', labelKey: 'Descricao', visible: true, fixed: false, defaultVisible: true },
    { key: 'informacao', labelKey: 'Informacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'pessoa', labelKey: 'Pessoa', visible: true, fixed: false, defaultVisible: true },
    { key: 'tipoTransacao', labelKey: 'Tipo Transacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'entrada', labelKey: 'Entrada', visible: true, fixed: false, defaultVisible: true },
    { key: 'saida', labelKey: 'Saida', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'Ações', visible: true, fixed: true, defaultVisible: true },
  ];

  /** Verifica se uma coluna esta visivel pelo key - OTIMIZAÇÃO 3: Com cache */
  isColumnVisible(key: string): boolean {
    if (this.columnVisibilityCache.has(key)) {
      return this.columnVisibilityCache.get(key)!;
    }
    const col = this.tableColumns.find(c => c.key === key);
    const visible = col ? col.visible : true;
    this.columnVisibilityCache.set(key, visible);
    return visible;
  }

  /** Callback quando colunas mudam via ColumnSelector */
  onColumnsChange(columns: TableColumn[]): void {
    this.tableColumns = columns;
    // OTIMIZAÇÃO 3: Limpa cache quando colunas mudam
    this.columnVisibilityCache.clear();
  }

  /** Conta colunas visiveis (para colspan do emptymessage/footer) */
  get visibleColumnCount(): number {
    return this.tableColumns.filter(c => c.visible).length;
  }

  /** Colspan para o label "TOTAIS DO PERIODO" no footer
   *  Conta colunas visiveis excluindo entrada, saida e saldo */
  get footerLabelColspan(): number {
    const excludeKeys = ['entrada', 'saida', 'acoes'];
    return this.tableColumns.filter(c => c.visible && !excludeKeys.includes(c.key)).length;
  }

  constructor(
    private baixaService: BaixaService,
    private contaService: ContaService,
    private empresaService: EmpresaConfigService,
    private messageService: MessageService,
    private translate: TranslateService,
    private tipoDespesaService: TipoDespesaService,
    private tipoReceitaService: TipoReceitaService,
    private route: ActivatedRoute,
    private router: Router,
    private permissaoService: PermissaoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    this.loadTranslations();
    this.loadColumnPreferences();
    this.buildTipoTransacaoOptions();
    this.buildTiposDespesaOptions();
    this.buildTiposReceitaOptions();
    this.filtroPessoaSelectorConfig = {
      categoryFilter: '',
      placeholder: this.translate.instant('movimentacao.placeholders.selecionar'),
      showAddButton: false,
      showAddInDropdown: this.permissaoService.temPermissao('cliente'),
      dialogTitle: this.translate.instant('movimentacao.labels.pessoa'),
      dialogIcon: 'pi pi-users',
    };
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildTipoTransacaoOptions();
      this.buildTiposDespesaOptions();
      this.buildTiposReceitaOptions();
    });

    // Só carrega dados no browser (evita chamada duplicada do SSR)
    if (isPlatformBrowser(this.platformId)) {
      this.carregarDadosIniciais();
      this.loadTiposDespesaMap();
      this.loadTiposReceitaMap();
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('movimentacao.texts.titulo'),
      subtitulo: this.translate.instant('movimentacao.texts.subtitulo'),
      filtros: this.translate.instant('movimentacao.texts.filtros'),
      conta: this.translate.instant('movimentacao.texts.conta'),
      selecione_periodo: this.translate.instant('movimentacao.texts.selecione_periodo'),
      total_guarani: this.translate.instant('movimentacao.texts.total_guarani'),
      total_dolar: this.translate.instant('movimentacao.texts.total_dolar'),
      total_real: this.translate.instant('movimentacao.texts.total_real'),
      total_peso: this.translate.instant('movimentacao.texts.total_peso'),
      saldo_positivo: this.translate.instant('movimentacao.texts.saldo_positivo'),
      saldo_negativo: this.translate.instant('movimentacao.texts.saldo_negativo'),
      movimentacoes: this.translate.instant('movimentacao.texts.movimentacoes'),
      totais_do_periodo: this.translate.instant('movimentacao.texts.totais_do_periodo'),
      nenhuma_movimentacao: this.translate.instant('movimentacao.texts.nenhuma_movimentacao'),
      ajuste_filtros: this.translate.instant('movimentacao.texts.ajuste_filtros'),
      carregando: this.translate.instant('movimentacao.texts.carregando'),
      processando: this.translate.instant('movimentacao.texts.processando'),
      sem_nome: this.translate.instant('movimentacao.texts.sem_nome'),
      nao_identificado: this.translate.instant('movimentacao.texts.nao_identificado'),
      filtros_avancados: this.translate.instant('movimentacao.texts.filtros_avancados'),
      filtros_avancados_subtitulo: this.translate.instant('movimentacao.texts.filtros_avancados_subtitulo'),
    };

    this.labels = {
      data_emissao: this.translate.instant('movimentacao.labels.data_emissao'),
      codigo: this.translate.instant('movimentacao.labels.codigo'),
      descricao: this.translate.instant('movimentacao.labels.descricao'),
      informacao: this.translate.instant('movimentacao.labels.informacao'),
      pessoa: this.translate.instant('movimentacao.labels.pessoa'),
      tipo_transacao: this.translate.instant('movimentacao.labels.tipo_transacao'),
      entrada: this.translate.instant('movimentacao.labels.entrada'),
      saida: this.translate.instant('movimentacao.labels.saida'),
      filtro_tipo_despesa: this.translate.instant('movimentacao.labels.filtro_tipo_despesa'),
      filtro_tipo_receita: this.translate.instant('movimentacao.labels.filtro_tipo_receita'),
      acoes: this.translate.instant('movimentacao.labels.acoes'),
    };

    this.placeholders = {
      pesquisar: this.translate.instant('movimentacao.placeholders.pesquisar'),
      selecionar: this.translate.instant('movimentacao.placeholders.selecionar'),
    };

    this.tooltips = {
      editar: this.translate.instant('movimentacao.tooltips.editar'),
      ver_fatura: this.translate.instant('movimentacao.tooltips.ver_fatura'),
    };

    this.botoes = {
      buscar: this.translate.instant('movimentacao.botoes.buscar'),
      imprimir_relatorio: this.translate.instant('movimentacao.botoes.imprimir_relatorio'),
      limpar_filtros: this.translate.instant('movimentacao.botoes.limpar_filtros'),
      filtrar: this.translate.instant('movimentacao.botoes.filtrar'),
    };

    // Atualiza labelKeys das colunas da tabela
    this.tableColumns = [
      { key: 'emissao', labelKey: this.labels.data_emissao, visible: true, fixed: true, defaultVisible: true },
      { key: 'codigoBaixa', labelKey: this.labels.codigo, visible: true, fixed: false, defaultVisible: true },
      { key: 'descricao', labelKey: this.labels.descricao, visible: true, fixed: false, defaultVisible: true },
      { key: 'informacao', labelKey: this.labels.informacao, visible: true, fixed: false, defaultVisible: true },
      { key: 'pessoa', labelKey: this.labels.pessoa, visible: true, fixed: false, defaultVisible: true },
      { key: 'tipoTransacao', labelKey: this.labels.tipo_transacao, visible: true, fixed: false, defaultVisible: true },
      { key: 'entrada', labelKey: this.labels.entrada, visible: true, fixed: false, defaultVisible: true },
      { key: 'saida', labelKey: this.labels.saida, visible: true, fixed: false, defaultVisible: true },
      { key: 'acoes', labelKey: this.labels.acoes, visible: true, fixed: true, defaultVisible: true },
    ];
  }

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    this.buscarBaixas();
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.tableColumns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
        this.columnVisibilityCache.clear();
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  carregarDadosIniciais(): void {
    this.loading = true;
    this.loadingContas = true;

    // Lê queryParams do dashboard (contaId, dataInicial, dataFinal)
    const qp = this.route.snapshot.queryParams;
    const qpContaId = qp['contaId'] ? Number(qp['contaId']) : null;
    const qpDataInicial = qp['dataInicial'] || null;
    const qpDataFinal = qp['dataFinal'] || null;

    // forkJoin dispara as duas requisições SIMULTANEAMENTE
    forkJoin({
      contas: this.contaService.get('/conta'),
      empresa: this.empresaService.getById('/configuracaoEmpresa/sessao')
    }).subscribe({
      next: (resultado) => {
        // --- 1. PROCESSAR CONTAS (otimizado) ---
        const resContas: any = resultado.contas;
        // contasFull preserva TODAS as contas (sem filtro por moedasPermitidas)
        // pois e usado em lookups internos (onContaChange, isMoedaSelecionada,
        // contaBancariaAtiva, moedaContaSelecionada, filtrarPorMoeda).
        this.contasFull = resContas.content;

        // --- 2. PROCESSAR EMPRESA E SELECIONAR CONTA ---
        const config: any = resultado.empresa;
        this.empresa = config;

        // Conjunto de moedas permitidas pela moeda padrao da empresa
        // (apenas filtra o que o usuario ve no dropdown).
        const permitidas = this.moedasPermitidas;

        // Processa contas filtrando pelas moedas permitidas.
        // contasFull continua intacto; somente o array do dropdown e reduzido.
        this.contas = this.contasFull
          .filter((c: any) => permitidas.has(((c.moeda?.enum ?? c.moeda) || '').toString()))
          .map((c: any) => ({
            label: c.numeroConta == 0 ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
            value: c.id,
            flagUrl: this.getFlagUrlByMoedaEnum(c.moeda?.enum)
          }));

        // Ordenar: conta DINHEIRO da moeda padrao da empresa aparece primeiro
        const moedaPadraoEnum = (resultado.empresa as any)?.moeda?.enum;
        if (moedaPadraoEnum) {
          this.contas.sort((a, b) => {
            const contaA = this.contasFull.find(c => c.id === a.value);
            const contaB = this.contasFull.find(c => c.id === b.value);
            const isTopA = contaA && (contaA.tipoConta === 'DINHEIRO' || contaA.tipoConta?.enum === 'DINHEIRO') && contaA.moeda?.enum === moedaPadraoEnum ? 1 : 0;
            const isTopB = contaB && (contaB.tipoConta === 'DINHEIRO' || contaB.tipoConta?.enum === 'DINHEIRO') && contaB.moeda?.enum === moedaPadraoEnum ? 1 : 0;
            return isTopB - isTopA;
          });
        }

        this.loadingContas = false;

        // Se veio contaId do dashboard, usa ele; senão, seleciona pela moeda padrão
        if (qpContaId && this.contasFull.find(c => c.id === qpContaId)) {
          this.contaSelecionada = qpContaId;
          this.filters.contaId = qpContaId;
          // Pré-seleciona o card de moeda correspondente à conta vinda do dashboard
          const contaDoQp = this.contasFull.find(c => c.id === qpContaId);
          if (contaDoQp?.moeda?.enum) {
            this.filtroMoedaSelecionada = contaDoQp.moeda.enum;
          }
        } else {
          const moedaPadraoEnum = config.moeda?.enum;
          if (moedaPadraoEnum && this.contasFull.length > 0) {
            // Busca preferencialmente a conta de moeda (numeroConta == 0) para a moeda padrão
            let contaAlvo = this.contasFull.find(c =>
              c.moeda?.enum === moedaPadraoEnum && c.numeroConta == 0
            ) || this.contasFull.find(c => c.moeda?.enum === moedaPadraoEnum);

            if (contaAlvo) {
              this.contaSelecionada = contaAlvo.id;
              this.filters.contaId = contaAlvo.id;
              // Pré-seleciona o card de moeda correspondente à moeda padrão da empresa
              this.filtroMoedaSelecionada = moedaPadraoEnum;
            }
          }
        }

        // --- 3. CONFIGURAR FILTROS DE DATA ---
        // Se veio datas do dashboard, usa elas; senão, usa período padrão (today)
        if (qpDataInicial && qpDataFinal) {
          this.aplicarDatasDoQueryParams(qpDataInicial, qpDataFinal);
        } else {
          this.aplicarPeriodoPadrao();
        }

        // --- 4. ATUALIZAR CARDS DE TOTAIS (após datas e conta estarem configurados) ---
        this.atualizarCardsSaldos();

        // --- 5. BUSCAR BAIXAS (agora com conta já selecionada) ---
        this.buscarBaixas();
      },
      error: (error) => {
        console.error("Erro ao carregar dados iniciais:", error);
        this.loading = false;
        this.loadingContas = false;
        this.aplicarPeriodoPadrao();
        this.buscarBaixas();
      }
    });
  }

  /**
   * Aplica o periodo padrao (today) calculando as datas via utils
   * e atualizando os filtros internos.
   */
  private aplicarPeriodoPadrao(): void {
    const today = new Date();
    const labels: Record<TipoPeriodo, string> = {
      untilToday: '', yesterdayAndToday: '', thisWeek: '', thisMonth: '',
      untilMonthEnd: '', prevMonth: '', nextMonth: '', last30: '',
      today: '', yesterday: '', showAll: '', custom: ''
    };
    // Extrato de Caixa NAO persiste e NAO segue o default "Mostrar todos" das
    // demais telas: abre sempre em "Hoje" (decisao do techlead).
    const result = calcularPeriodoPredefinido('today', today, labels, 0, 0, 23, 59);
    this.filters.dataInicio = fmtDateTime(result.start);
    this.filters.dataFim = fmtDateTime(result.end);
    this.periodoSelecionado = {
      tipo: 'today',
      dataInicio: this.filters.dataInicio,
      dataFim: this.filters.dataFim,
      texto: ''
    };
  }

  /**
   * Aplica datas vindas dos queryParams do dashboard.
   * O formato dos queryParams (dd/MM/yyyyTHH:mm) e o mesmo usado pelo periodo-filtro,
   * portanto podemos usa-los diretamente nos filtros.
   */
  private aplicarDatasDoQueryParams(dataInicial: string, dataFinal: string): void {
    this.filters.dataInicio = dataInicial;
    this.filters.dataFim = dataFinal;
    this.periodoSelecionado = {
      tipo: 'custom',
      dataInicio: dataInicial,
      dataFim: dataFinal,
      texto: ''
    };
  }

  /**
   * Callback do componente app-periodo-filtro.
   * Recebe o PeriodoFiltro selecionado pelo usuario e atualiza os filtros internos.
   */
  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.periodoSelecionado = periodo;
    this.filters.dataInicio = periodo.dataInicio;
    this.filters.dataFim = periodo.dataFim;
  }

buscarBaixas(): void {
  if (this.contaSelecionada === null) {
    return;
  }

  this.loading = true;
  this.filters.buscaGeral = this.searchNome;
  this.filters.contaId = this.contaSelecionada;

  // Monta array de tipoDeTransacao se filtro ativo
  const tipoTransacaoArr = this.filtroTipoTransacao ? [this.filtroTipoTransacao] : null;

  // Sem paginacao: carrega todos os movimentos do periodo em uma unica chamada
  // (page=0, size=SIZE_SEM_PAGINACAO). O footer de totais reflete o periodo inteiro.
  this.baixaService.buscarPorFiltros(
    this.filters.contaId,
    this.filters.dataInicio,
    this.filters.dataFim,
    this.filters.buscaGeral,
    tipoTransacaoArr,
    this.filtroTipoDespesa,
    this.filtroTipoReceita,
    this.filtroPessoaId,
    0,
    this.SIZE_SEM_PAGINACAO,
    `${this.lastfield},${this.lastSort}`
  ).subscribe({
    next: (res: BaixaResponseDTO) => {
      this.baixas = res.content || [];
      this.loading = false;

      // Calcular totais de entradas e saidas de todo o periodo carregado
      this.totalEntradasPeriodo = 0;
      this.totalSaidasPeriodo = 0;

      this.baixas.forEach((b: any) => {
        if (this.isEntrada(b)) {
          this.totalEntradasPeriodo += Math.abs(b.valor);
        } else if (this.isSaida(b)) {
          this.totalSaidasPeriodo += Math.abs(b.valor);
        }
      });
    },
    error: (error) => {
      this.loading = false;
      console.error("Erro ao buscar baixas:", error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro_servidor'),
      });
    }
  });
}

  /**
   * Gera o PDF do Extrato de Caixa reaproveitando os filtros ativos da tela.
   * Reusa o mesmo estado que alimenta a lista exibida (conta, periodo, busca,
   * tipo de transacao, pessoa, tipo de gasto/ingresso) para que o PDF reflita
   * exatamente o que esta na tela.
   */
  exportarPdf(): void {
    if (!this.contaSelecionada || this.exportandoPdf) {
      return;
    }

    this.exportandoPdf = true;

    // Monta os params a partir do estado da tela (conversao FormDTO->DTO).
    // tipoDeTransacao e single-element na tela; serializado como CSV no params.
    const tipoTransacaoArr = this.filtroTipoTransacao ? [this.filtroTipoTransacao] : null;

    const params: ExtratoDeCaixaPdfParams = {
      conta: this.contaSelecionada,
    };
    if (this.filters.dataInicio) params.dataInicio = this.filters.dataInicio;
    if (this.filters.dataFim) params.dataFim = this.filters.dataFim;
    if (this.filters.buscaGeral) params.buscaGeral = this.filters.buscaGeral;
    if (tipoTransacaoArr && tipoTransacaoArr.length > 0) {
      params.tipoDeTransacao = tipoTransacaoArr.join(',');
    }
    if (this.filtroPessoaId != null) params.pessoaId = this.filtroPessoaId;
    if (this.filtroTipoDespesa) params.tipoDespesa = this.filtroTipoDespesa;
    if (this.filtroTipoReceita) params.tipoReceita = this.filtroTipoReceita;
    // origemTransacao: omitido (sem fonte de estado na tela).

    this.baixaService.exportarExtratoDeCaixaPdf(params).subscribe({
      next: (base64: string) => {
        FileUtils.abrirPdf(base64, 'extrato-de-caixa.pdf');
        this.exportandoPdf = false;
      },
      error: (err) => {
        this.exportandoPdf = false;
        if (err?.status === 404) {
          // Sem dados no recorte (o path esta correto; 404 = nenhuma operacao).
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.aviso'),
            detail: this.translate.instant('relatorios.mensagens.nenhum_dado_encontrado'),
          });
        } else if (err?.status === 422) {
          // Conta ausente (defense-in-depth; o botao ja bloqueia).
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.aviso'),
            detail: this.translate.instant('movimentacao.mensagens.selecione_conta_extrato'),
          });
        } else {
          // 400 / 401 / 500 / rede.
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('relatorios.mensagens.erro_gerar_relatorio'),
          });
        }
      },
    });
  }

  customSort(event: SortEvent): void {
    if (!event.field) return;
    const field = event.field;
    const novoSort = (event.order ?? 1) === 1 ? 'asc' : 'desc';

    // Guarda contra re-emissao: ignora se campo e direcao nao mudaram
    if (field === this.lastfield && novoSort === this.lastSort) {
      return;
    }

    // Atualiza os parametros de ordenacao e dispara nova chamada a API
    // para que o sort seja aplicado sobre todos os registros do periodo.
    this.lastfield = field;
    this.lastSort = novoSort;
    this.buscarBaixas();
  }

  onContaChange(): void {
    this.filters.contaId = this.contaSelecionada;
    // Sincroniza o card de moeda destacado com a moeda da conta selecionada
    if (this.contaSelecionada) {
      const conta = this.contasFull.find(c => c.id === this.contaSelecionada);
      if (conta?.moeda?.enum) {
        this.filtroMoedaSelecionada = conta.moeda.enum;
      }
    }
    this.atualizarCardsSaldos();
    this.buscarBaixas();
  }

  /**
   * Busca os totais por moeda usando o endpoint /baixa/total-baixas
   * (mesma abordagem do app mobile - 1 unica chamada).
   */
  atualizarCardsSaldos(): void {
    this.totalGuarani = 0;
    this.totalDolar = 0;
    this.totalReal = 0;
    this.totalPeso = 0;
    this.loadingTotais = true;

    const tipoTransacaoArr = this.filtroTipoTransacao ? [this.filtroTipoTransacao] : null;

    this.baixaService.totalBaixas(
      this.contaSelecionada,
      this.filters.dataInicio,
      this.filters.dataFim,
      tipoTransacaoArr,
      this.filtroTipoDespesa,
      this.filtroTipoReceita,
      this.filtroPessoaId
    ).subscribe({
      next: (totais) => {
        this.totalGuarani = totais.valorTotalEmGuarani || 0;
        this.totalDolar = totais.valorTotalEmDolar || 0;
        this.totalReal = totais.valorTotalEmReal || 0;
        this.totalPeso = totais?.valorTotalEmPeso || 0;
        this.loadingTotais = false;
      },
      error: () => {
        this.totalPeso = 0;
        this.loadingTotais = false;
      },
    });
  }

  isMoedaSelecionada(moedaEnum: string): boolean {
    if (!this.contaSelecionada) return false;
    const conta = this.contasFull.find(c => c.id === this.contaSelecionada);
    return conta?.moeda?.enum === moedaEnum;
  }

  /**
   * Retorna true quando a conta selecionada é uma conta bancária (tipoConta === 'BANCO').
   * Neste caso, os cards de moeda ficam desabilitados (mesmo comportamento do mobile).
   */
  get contaBancariaAtiva(): boolean {
    if (!this.contaSelecionada) return false;
    const conta = this.contasFull.find(c => c.id === this.contaSelecionada);
    const tipo = conta?.tipoConta;
    const tipoStr = typeof tipo === 'object' ? (tipo as any)?.enum : tipo;
    return tipoStr === 'BANCO';
  }

  /** Retorna o enum da moeda da conta selecionada (GUARANI, DOLAR, REAL) ou null. */
  get moedaContaSelecionada(): string | null {
    if (!this.contaSelecionada) return null;
    const conta = this.contasFull.find(c => c.id === this.contaSelecionada);
    return conta?.moeda?.enum || null;
  }

  /**
   * Conjunto de moedas permitidas para esta empresa, baseado na moeda padrao
   * configurada e na matriz de pareamento (MOEDAS_COMPATIVEIS).
   * Quando a moeda padrao da empresa nao esta definida, libera as 4 moedas.
   */
  get moedasPermitidas(): Set<string> {
    const moedaEmp = this.empresa?.moeda
      ? ((this.empresa.moeda as any).enum ?? this.empresa.moeda) as string
      : null;
    if (!moedaEmp) return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    return new Set(moedasCompativeis(moedaEmp as any));
  }


  search(): void {
    this.atualizarCardsSaldos();
    this.buscarBaixas();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  getIcone(b: BaixaDTO): string {
    if (this.isEntrada(b)) return 'pi pi-arrow-up text-green-600';
    if (this.isSaida(b)) return 'pi pi-arrow-down text-red-600';
    return 'pi pi-exchange';
  }

  getOperacaoColor(b: BaixaDTO): string {
    if (this.isEntrada(b)) return 'text-green-600 font-bold';
    if (this.isSaida(b)) return 'text-red-600 font-bold';
    return '';
  }

  isEntrada(b: BaixaDTO): boolean {
    const tipo = typeof b.tipoOperacaoFinanceira === 'string'
      ? b.tipoOperacaoFinanceira
      : b.tipoOperacaoFinanceira?.descricao;

    if (!tipo) return b.valor > 0;

    return tipo.toString().toUpperCase() === 'ENTRADA';
  }

  isSaida(b: BaixaDTO): boolean {
    const tipo = typeof b.tipoOperacaoFinanceira === 'string'
      ? b.tipoOperacaoFinanceira
      : b.tipoOperacaoFinanceira?.descricao;

    if (!tipo) return b.valor < 0;


    const tipoNormalizado = tipo.toString().toUpperCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    return tipoNormalizado === 'SAIDA';
  }

  getTipoTransacaoDescricao(b: BaixaDTO): string {
    const enumValue = this.getTipoTransacaoEnum(b);
    if (!enumValue) return '';
    const chave = `enums.tipo_transacao.${enumValue}`;
    const traduzido = this.translate.instant(chave);
    // Se a chave não existir no i18n, retorna o valor do enum como fallback
    return traduzido !== chave ? traduzido : enumValue;
  }

  getTipoOperacaoDescricao(b: BaixaDTO): string {
    const tipo = b.tipoOperacaoFinanceira;
    if (!tipo) return '';
    return typeof tipo === 'string' ? tipo : tipo.descricao ?? '';
  }

  /**
   * Retorna o label traduzido do subtipo específico da baixa
   * (tipo de receita ou tipo de despesa). null se não houver subtipo.
   * Fallback triplo: chave i18n → nome traduzido vindo do backend → enum cru.
   */
  getSubtipoLabel(b: BaixaDTO): string | null {
    const enumReceita = b.tipoDeReceita?.enum;
    if (enumReceita) {
      const chave = `nova-receita.tipos_receita.${enumReceita}`;
      const traduzido = this.translate.instant(chave);
      if (traduzido && traduzido !== chave) {
        return traduzido;
      }
      return b.tipoDeReceita?.nome ?? enumReceita;
    }

    const enumDespesa = b.tipoDeDespesa?.enum;
    if (enumDespesa) {
      const chave = `enums.tipo_despesa.${enumDespesa}`;
      const traduzido = this.translate.instant(chave);
      if (traduzido && traduzido !== chave) {
        return traduzido;
      }
      return b.tipoDeDespesa?.nome ?? enumDespesa;
    }

    return null;
  }

  /**
   * Retorna a cor (hex) associada ao subtipo, vinda do backend.
   * null se não houver subtipo ou cor.
   */
  getSubtipoCor(b: BaixaDTO): string | null {
    return b.tipoDeReceita?.cor ?? b.tipoDeDespesa?.cor ?? null;
  }


  getEntradaValue(b: BaixaDTO): number | null {
    return this.isEntrada(b) ? Math.abs(b.valor) : null;
  }

  getSaidaValue(b: BaixaDTO): number | null {
    return this.isSaida(b) ? Math.abs(b.valor) : null;
  }

  getMoedaCode(b: BaixaDTO | any): string {
    return b.moeda?.codigoOperacao || 'PYG';
  }

  getMoedaSymbol(b: BaixaDTO | any): string {
    const codigo = b.moeda?.codigoOperacao;
    switch (codigo) {
      case 'USD': return 'US$ ';
      case 'BRL': return 'R$ ';
      default: return 'Gs ';
    }
  }

  getMoedaDigits(b: BaixaDTO | any): string {
    if (b.moeda && typeof b.moeda.casasDecimais === 'number') {
      const casas = b.moeda.casasDecimais;
      return `1.${casas}-${casas}`;
    }

    return (b.moeda?.codigoOperacao === 'PYG') ? '1.0-0' : '1.2-2';
  }

  getMoedaView(): { code: string, symbol: string, digits: string } {
    if (this.baixas && this.baixas.length > 0) {
      const b = this.baixas[0];
      return {
        code: this.getMoedaCode(b),
        symbol: this.getMoedaSymbol(b),
        digits: this.getMoedaDigits(b)
      };
    }
    // Fallback: usa moeda da conta selecionada
    const moedaEnum = this.moedaContaSelecionada;
    if (moedaEnum === 'REAL') {
      return { code: 'BRL', symbol: 'R$ ', digits: '1.2-2' };
    } else if (moedaEnum === 'DOLAR') {
      return { code: 'USD', symbol: 'US$ ', digits: '1.2-2' };
    } else if (moedaEnum === 'PESO') {
      return { code: 'ARS', symbol: '$ ', digits: '1.2-2' };
    }
    return { code: 'PYG', symbol: 'Gs ', digits: '1.0-0' };
  }

  formatarValorMonetario(valor: number | null, moedaObj: any): string {
    if (valor == null || isNaN(valor)) return '-';

    const codigo = moedaObj?.codigoOperacao || 'PYG';

    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      'PYG': { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      'BRL': { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      'USD': { locale: 'en-US', simbolo: '$', decimais: true },
      'ARS': { locale: 'es-AR', simbolo: '$', decimais: true },
    };

    const config = configuracoes[codigo] ?? configuracoes['PYG'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };

    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(Math.abs(valor));
    return `${config.simbolo} ${valorFormatado}`;
  }

  formatarValorView(valor: number | null): string {
    if (valor == null || isNaN(valor)) return '-';
    const view = this.getMoedaView();
    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      'PYG': { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      'BRL': { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      'USD': { locale: 'en-US', simbolo: '$', decimais: true },
      'ARS': { locale: 'es-AR', simbolo: '$', decimais: true },
    };
    const config = configuracoes[view.code] ?? configuracoes['PYG'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(Math.abs(valor));
    return `${config.simbolo} ${valorFormatado}`;
  }


  /** Retorna a URL da bandeira do pais baseado no enum da moeda */
  getFlagUrlByMoedaEnum(moedaEnum: string): string {
    const moedaStr = (moedaEnum || '').toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  /**
   * Callback do periodo quando usado no header da tabela (mesmo nome do nova-despesa).
   */
  onPeriodoDespesaChanged(periodo: PeriodoFiltro): void {
    this.periodoSelecionado = periodo;
    this.filters.dataInicio = periodo.dataInicio;
    this.filters.dataFim = periodo.dataFim;
    this.atualizarCardsSaldos();
    this.buscarBaixas();
  }

  /**
   * Toggle para filtrar por moeda nos cards de totais.
   * Clica na mesma moeda = desativa o filtro.
   */
  filtrarPorMoeda(moeda: string): void {
    // Bloquear clique quando conta bancaria esta ativa
    if (this.contaBancariaAtiva) {
      return;
    }
    // Se ja esta selecionada, manter o filtro (sem toggle)
    if (this.filtroMoedaSelecionada === moeda) {
      return;
    }
    this.filtroMoedaSelecionada = moeda;
    // Seleciona automaticamente a conta EFETIVO da moeda clicada (mesmo comportamento do mobile).
    // EFETIVO (numeroConta == 0) e sempre a prioridade.
    // Usa conta bancaria apenas como fallback quando nao ha EFETIVO para a moeda.
    if (this.filtroMoedaSelecionada) {
      const contaEfetivo = this.contasFull.find(
        c => c.moeda?.enum === this.filtroMoedaSelecionada && c.numeroConta == 0
      );
      const contaBancaria = this.contasFull.find(
        c => c.moeda?.enum === this.filtroMoedaSelecionada && c.numeroConta != 0
      );
      // Prioriza EFETIVO; se nao houver, usa conta bancaria como fallback
      const contaDaMoeda = contaEfetivo ?? contaBancaria;
      if (contaDaMoeda) {
        this.contaSelecionada = contaDaMoeda.id;
        this.onContaChange();
        return;
      }
    }
    // Se desativou filtro, recarrega com conta atual
    this.buscarBaixas();
  }

  /**
   * Limpa filtros avancados da sidebar.
   */
  limparFiltrosAvancados(): void {
    this.filtroDescricao = '';
    this.filtroTipoTransacao = null;
    this.filtroTipoDespesa = null;
    this.filtroTipoReceita = null;
    this.filtroPessoaId = null;
    this.filtroPessoaSelecionada = undefined;
    this.searchNome = '';
    this.filters.buscaGeral = '';
    this.atualizarCardsSaldos();
    this.buscarBaixas();
  }

  onFiltroPessoaSelecionada(pessoa: PessoaDTO): void {
    this.filtroPessoaSelecionada = pessoa;
    this.filtroPessoaId = pessoa?.id ?? null;
  }

  onFiltroPessoaLimpa(): void {
    this.filtroPessoaSelecionada = undefined;
    this.filtroPessoaId = null;
  }

  // Controle do dialog de adicionar pessoa
  visibleDialogPessoa = false;

  abrirDialogoPessoa(): void {
    this.visibleDialogPessoa = true;
  }

  /** Callback: pessoa criada ou vinculada no dialog — seleciona automaticamente no filtro */
  onPessoaCriadaNoDialog(pessoa: PessoaDTO): void {
    this.filtroPessoaSelecionada = pessoa;
    this.filtroPessoaId = pessoa?.id ?? null;
  }

  /**
   * Aplica filtros avancados da sidebar.
   * Filtros de tipo de gasto e tipo de ingresso sao aplicados localmente
   * apos o carregamento dos dados paginados.
   */
  aplicarFiltrosAvancados(): void {
    // Combina descricao do sidebar com busca geral
    this.searchNome = this.filtroDescricao || '';
    this.atualizarCardsSaldos();
    this.buscarBaixas();
  }

  /**
   * Constroi opcoes de tipo de transacao para o dropdown do sidebar.
   */
  private buildTipoTransacaoOptions(): void {
    this.tipoTransacaoOptions = this.tiposTransacao.map(t => {
      const chave = `enums.tipo_transacao.${t}`;
      const traduzido = this.translate.instant(chave);
      return {
        label: traduzido !== chave ? traduzido : t,
        value: t
      };
    });
  }

  /**
   * Constroi opcoes de tipo de despesa para o dropdown do sidebar.
   */
  private buildTiposDespesaOptions(): void {
    this.tiposDespesaFiltro = Object.values(TipoDeDespesa)
      .filter(v => !TIPOS_DESPESA_NAO_EXIBIR.includes(v))
      .map((value) => ({
        label: this.translate.instant(`enums.tipo_despesa.${value}`),
        value: value as string,
        icone: this.tipoDespesaMap.get(value)?.icone || '',
        cor: this.tipoDespesaMap.get(value)?.cor || '',
      }));
  }

  /**
   * Constroi opcoes de tipo de receita para o dropdown do sidebar.
   */
  private buildTiposReceitaOptions(): void {
    this.tiposReceitaFiltro = Object.values(TipoDeReceita).map((value) => ({
      label: this.translate.instant(`nova-receita.tipos_receita.${value}`),
      value: value as string,
      icone: this.tipoReceitaMap.get(value)?.icone || '',
      cor: this.tipoReceitaMap.get(value)?.cor || '',
    }));
  }

  /**
   * Carrega tipos de despesa do backend para obter icone e cor.
   */
  private loadTiposDespesaMap(): void {
    this.tipoDespesaService.get<any>('/enums/tipoDespesa', {
      size: 50,
      sort: 'nome,asc',
    }).subscribe({
      next: (response: any) => {
        const tipos = Array.isArray(response) ? response : response?.content || [];
        tipos.forEach((tipo: any) => {
          const chave = tipo.enum || tipo.nome;
          if (chave) {
            this.tipoDespesaMap.set(chave, {
              icone: tipo.icone || '',
              cor: tipo.cor || '',
            });
          }
        });
        // Enriquece dropdown com icones
        this.buildTiposDespesaOptions();
      },
      error: () => {
        // Silencioso - dropdown funciona sem icones
      },
    });
  }

  /**
   * Carrega tipos de receita do backend (fonte da verdade).
   * Popula o dropdown diretamente com a resposta, alinhando o web ao mobile
   * e evitando divergencia com o enum local TypeScript (que fica desatualizado).
   */
  private loadTiposReceitaMap(): void {
    this.tipoReceitaService.get<any>('/enums/tipoReceita', {
      size: 100,
      sort: 'nome,asc',
    }).subscribe({
      next: (response: any) => {
        const tipos = Array.isArray(response) ? response : response?.content || [];

        // Popula o map de icones/cores (usado no rendering das linhas da tabela).
        tipos.forEach((tipo: any) => {
          const chave = tipo.enum || tipo.nome;
          if (chave) {
            this.tipoReceitaMap.set(chave, {
              icone: tipo.icone || '',
              cor: tipo.cor || '',
            });
          }
        });

        // Constroi as opcoes do dropdown a partir do backend, filtrando os
        // tipos com exibir=false e usando o nome ja traduzido pelo backend
        // (com fallback para a chave i18n do frontend).
        const opcoes = tipos
          .filter((tipo: any) => tipo?.exibir !== false && (tipo?.enum || tipo?.nome))
          .map((tipo: any) => {
            const value = (tipo.enum || tipo.nome) as string;
            const chaveI18n = `nova-receita.tipos_receita.${value}`;
            const traduzido = this.translate.instant(chaveI18n);
            const label = traduzido && traduzido !== chaveI18n
              ? traduzido
              : (tipo.nome || value);
            return {
              label,
              value,
              icone: tipo.icone || '',
              cor: tipo.cor || '',
            };
          })
          .sort((a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label),
          );

        if (opcoes.length > 0) {
          this.tiposReceitaFiltro = opcoes;
        } else {
          // Fallback: mantem opcoes do enum local caso o backend retorne vazio.
          this.buildTiposReceitaOptions();
        }
      },
      error: () => {
        // Silencioso - mantem as opcoes do enum local como fallback.
      },
    });
  }

  /**
   * Retorna o enum do tipo de transacao da baixa (string normalizada).
   */
  private getTipoTransacaoEnum(b: BaixaDTO): string {
    if (typeof b.tipoDeTransacao === 'string') {
      return b.tipoDeTransacao.toUpperCase();
    }
    return (b.tipoDeTransacao?.enum || b.tipoDeTransacao?.descricao || '').toUpperCase();
  }

  /** Tipos de transacao que representam faturas/notas (VENDA, COMPRA, devoluções). */
  private readonly TIPOS_FATURA: string[] = ['VENDA', 'COMPRA', 'DEVOLUCAO', 'DEVOLUCAO_DE_COMPRA'];

  /**
   * Verifica se a baixa e do tipo fatura (VENDA, COMPRA, DEVOLUCAO, DEVOLUCAO_DE_COMPRA).
   */
  isTipoFatura(b: BaixaDTO): boolean {
    const tipo = this.getTipoTransacaoEnum(b);
    return this.TIPOS_FATURA.includes(tipo);
  }

  /**
   * Verifica se a baixa pode ser editada/visualizada (tem rota disponivel).
   * Para RECEITA, DESPESA e TRANSFERENCIA: precisa de movimento_id.
   * Para VENDA, COMPRA, DEVOLUCAO e DEVOLUCAO_DE_COMPRA: precisa de nota_id.
   */
  podeEditar(b: BaixaDTO): boolean {
    const tipo = this.getTipoTransacaoEnum(b);
    const notaId = b.nota_id ?? b.nota?.id;
    const movimentoId = b.movimento_id ?? b.movimento?.id;

    if (this.TIPOS_FATURA.includes(tipo)) {
      return !!notaId;
    }

    if (tipo === 'DESPESA' || tipo === 'RECEITA' || tipo === 'TRANSFERENCIA') {
      return !!movimentoId;
    }

    return false;
  }

  /**
   * Retorna o tooltip adequado ao tipo de acao (editar ou ver fatura).
   */
  getTooltipEditar(b: BaixaDTO): string {
    if (this.isTipoFatura(b)) {
      return this.tooltips.ver_fatura;
    }
    return this.tooltips.editar;
  }

  /**
   * Retorna o icone adequado ao tipo de acao (editar ou ver fatura).
   */
  getIconeEditar(b: BaixaDTO): string {
    if (this.isTipoFatura(b)) {
      return 'pi pi-file';
    }
    return 'pi pi-pencil';
  }

  /**
   * Navega para a tela de edicao correspondente ao tipo de transacao da baixa.
   * Para faturas (VENDA, COMPRA, devoluções): navega para a tela de fatura.
   * Para RECEITA, DESPESA e TRANSFERENCIA: navega para a tela de edicao do movimento.
   */
  editarBaixa(b: BaixaDTO): void {
    const tipo = this.getTipoTransacaoEnum(b);
    const notaId = b.nota_id ?? b.nota?.id;
    const movimentoId = b.movimento_id ?? b.movimento?.id;

    // Tipos de fatura: navegar para a tela de fatura usando nota_id
    if (this.TIPOS_FATURA.includes(tipo) && notaId) {
      this.router.navigate(['/notas-list/fatura', notaId]);
      return;
    }

    // Tipos de movimento: navegar para a tela de edicao usando movimento_id
    if (!movimentoId) return;

    switch (tipo) {
      case 'DESPESA':
        this.router.navigate(['/nova-despesa'], { queryParams: { editId: movimentoId } });
        break;
      case 'RECEITA':
        this.router.navigate(['/nova-receita'], { queryParams: { editId: movimentoId } });
        break;
      case 'TRANSFERENCIA':
        this.router.navigate(['/nova-transferencia'], { queryParams: { editId: movimentoId } });
        break;
      default:
        break;
    }
  }


}
